import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataCard, MoneyAmount, type ModuleVariant } from './index';

const CATEGORIE_LABEL: Record<string, string> = {
  honoraires_formateur: 'Honoraires formateur',
  hotel_lieu: 'Hôtel / Lieu',
  traiteur: 'Traiteur',
  transport: 'Transport',
  supports_impressions: 'Supports / Impressions',
  commission_commercial: 'Commission commercial',
  marketing_digital: 'Marketing digital',
  autre: 'Autres',
};

const CATEGORIE_COLOR: Record<string, string> = {
  honoraires_formateur: '#8b5cf6',
  hotel_lieu: '#06b6d4',
  traiteur: '#f59e0b',
  transport: '#10b981',
  supports_impressions: '#ef4444',
  commission_commercial: '#ec4899',
  marketing_digital: '#a855f7',
  autre: '#64748b',
};

export interface ChargeBreakdown {
  categorie: string;
  total: number;
}

export interface BenefitSimulatorCardProps {
  prixVenteHT: number;
  totalCharges: number;
  charges: ChargeBreakdown[];
  variant?: ModuleVariant;
  compact?: boolean;
}

export function BenefitSimulatorCard({
  prixVenteHT,
  totalCharges,
  charges,
  variant = 'formation_b2b',
  compact = false,
}: BenefitSimulatorCardProps): JSX.Element {
  const benefice = prixVenteHT - totalCharges;
  const margePct = prixVenteHT > 0 ? (benefice / prixVenteHT) * 100 : 0;
  const positif = benefice >= 0;

  const pieData = useMemo(
    () =>
      charges
        .filter((c) => c.total > 0)
        .map((c) => ({
          name: CATEGORIE_LABEL[c.categorie] ?? c.categorie,
          value: c.total,
          fill: CATEGORIE_COLOR[c.categorie] ?? '#64748b',
        })),
    [charges],
  );

  return (
    <DataCard variant="pastel" module={variant}>
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <h3 className="text-sm font-bold text-slate-800">Simulateur de bénéfice</h3>
      </div>

      <div className={cn('grid gap-4', compact ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-3')}>
        <Tile label="Recettes HT" tone="neutral">
          <MoneyAmount amount={prixVenteHT} variant="kpi" />
        </Tile>
        <Tile label="Charges totales" tone="negative">
          <MoneyAmount amount={totalCharges} variant="kpi" />
        </Tile>
        <Tile label="Bénéfice brut" tone={positif ? 'positive' : 'negative'}>
          <MoneyAmount amount={benefice} variant="kpi" withColor showSign />
          <div
            className={cn(
              'mt-0.5 inline-flex items-center gap-1 text-xs font-semibold',
              positif ? 'text-emerald-700' : 'text-rose-700',
            )}
          >
            {positif ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            Marge : {margePct.toFixed(1)}%
          </div>
        </Tile>
      </div>

      {pieData.length > 0 && !compact && (
        <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="h-48 sm:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) =>
                    `${new Intl.NumberFormat('fr-DZ').format(value)} DA`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 text-xs">
            {pieData.map((p) => (
              <li key={p.name} className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: p.fill }}
                />
                <span className="flex-1 text-slate-700">{p.name}</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {new Intl.NumberFormat('fr-DZ').format(p.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DataCard>
  );
}

function Tile({
  label,
  tone,
  children,
}: {
  label: string;
  tone: 'positive' | 'negative' | 'neutral';
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-xl bg-white/70 p-3 ring-1 backdrop-blur',
        tone === 'positive' && 'ring-emerald-200',
        tone === 'negative' && 'ring-rose-200',
        tone === 'neutral' && 'ring-slate-200',
      )}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
